import React, { PropsWithChildren } from 'react';
import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import { Field, FieldProps, Formik } from 'formik';
import * as Yup from 'yup';
import { gql } from '@yi/core';
import { i18n_playlist_description } from '@yi/i18n/phrases/i18n_playlist_description';
import { i18n_playlist_name } from '@yi/i18n/phrases/i18n_playlist_name';
import { i18n_required } from '@yi/i18n/phrases/i18n_required';

import { turquoise } from '~/styles';

import type { FetchResult } from '@apollo/client/link/core/types';

import { Button } from '../../buttons';
import { FormInput } from '../../FormInput';
import { useLocale } from '../../LocaleProvider';
import { Snackbar } from '../../Snackbar';

interface FormikValues {
  playlistName: string;
  playlistDescription: string;
}
export interface PlaylistModalProps {
  open: boolean;
  title?: string;
  buttonText: string;
  initialValue?: { title: string; description: string };
  contentTitle?: string;
  onSubmit: (
    values: FormikValues,
  ) => Promise<
    | FetchResult<gql.typesClient.Mutate_CreatePlaylist>
    | FetchResult<gql.typesClient.Mutate_EditPlaylist>
  >;
  setClose: () => void;
}

export const PlaylistModal: React.FC<PropsWithChildren<PlaylistModalProps>> = ({
  open = true,
  title = '',
  initialValue = { title: '', description: '' },
  buttonText,
  onSubmit,
  setClose,
  contentTitle,
}) => {
  const { locale } = useLocale();
  const validationSchema = Yup.object().shape({
    playlistName: Yup.string().required(i18n_required(locale)),
  });
  return (
    <Snackbar
      open={open}
      setClose={setClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      style={{
        left: '50%',
        top: '50%',
        transform: 'translateX(-50%) translateY(-50%)',
        marginLeft: 0,
      }}
      contentStyle={{ backgroundColor: 'white' }}
      ClickAwayListenerProps={{ onClickAway: setClose }}
    >
      <Box width="100%" height="100%" bgcolor="white" p="20px">
        <Box
          fontSize="22px"
          lineHeight="28px"
          fontWeight="700"
          color="#000"
          textAlign="center"
          style={{ textTransform: 'uppercase' }}
          mb="25px"
        >
          {title}
        </Box>

        <Formik
          initialValues={{
            playlistName: initialValue.title,
            playlistDescription: initialValue.description,
          }}
          onSubmit={async (values, { setSubmitting }) => {
            setSubmitting(true);
            await onSubmit(values);
            setClose();
            setSubmitting(false);
          }}
          validationSchema={validationSchema}
        >
          {({ handleSubmit, isSubmitting, errors, touched }) => (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <Box
                display="flex"
                alignItems="center"
                flexDirection="column"
                justifyContent="space-between"
              >
                <FormGroup>
                  <Field
                    label={i18n_playlist_name(locale)}
                    type="playlistName"
                    name="playlistName"
                    render={({ field }: FieldProps<FormikValues>) => (
                      <Box width={{ xs: '215px', md: '300px' }}>
                        <FormInput
                          onKeyDown={e => e.stopPropagation()}
                          data-testid={
                            contentTitle
                              ? `playlist-name-field-${contentTitle}`
                              : 'playlist-name-field'
                          }
                          style={{ minWidth: '100%' }}
                          {...field}
                          autoFocus
                          label={i18n_playlist_name(locale)}
                          type="text"
                          id="playlistName"
                          name="playlistName"
                          data-required="true"
                          error={touched.playlistName && Boolean(errors.playlistName)}
                          placeholder={i18n_playlist_name(locale)}
                          fullWidth
                          variant="filled"
                          helperText={
                            touched.playlistName && errors && errors.playlistName
                              ? errors.playlistName
                              : ''
                          }
                        />
                      </Box>
                    )}
                  />
                </FormGroup>
                <FormGroup>
                  <Field
                    label="Playlist Description"
                    type="playlistDescription"
                    name="playlistDescription"
                    render={({ field }: FieldProps<FormikValues>) => (
                      <Box width={{ xs: '215px', md: '300px' }}>
                        <FormInput
                          onKeyDown={e => e.stopPropagation()}
                          data-testid={
                            contentTitle
                              ? `playlist-description-field-${contentTitle}`
                              : 'playlist-description-field'
                          }
                          style={{ minWidth: '100%', textTransform: 'capitalize' }}
                          {...field}
                          autoFocus
                          multiline
                          rows={4}
                          label={i18n_playlist_description(locale)}
                          type="text"
                          id="playlistDescription"
                          name="playlistDescription"
                          data-required="true"
                          placeholder={i18n_playlist_description(locale)}
                          fullWidth
                          variant="filled"
                        />
                      </Box>
                    )}
                  />
                </FormGroup>
                <Box width={{ xs: '150px', md: '200px' }} mt="10px">
                  <Button
                    style={{ minWidth: '100%', height: '40px' }}
                    showSpinner={isSubmitting}
                    spinnerColor={turquoise}
                    type="submit"
                    data-testid="new-playlist-submit-button"
                  >
                    {buttonText}
                  </Button>
                </Box>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Snackbar>
  );
};
